import React from "react";
import { Link } from "react-router-dom";
import { siteConfig } from "../Datas/productData";
import "../css/privacy.scss";

const getAddress = (value) => {
  if (Array.isArray(value)) return value.join(", ");
  if (value && typeof value === "object") {
    return Object.values(value).filter(Boolean).join(", ");
  }
  return value || "";
};

function Privacy() {
  const contact = siteConfig.contact || {};
  const address = getAddress(contact.address);
  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="privacy-page">
      <header className="privacy-page__hero">
        <p>Legal</p>
        <h1>Privacy policy</h1>
        <div>
          <p>
            This policy explains how {siteConfig.brandName} collects, uses, and
            protects personal information when you visit or shop with us.
          </p>
          <p className="privacy-page__date">
            Last updated <time dateTime="2026-08-13">August 13, 2026</time>
          </p>
        </div>
      </header>

      <div className="privacy-page__layout">
        <aside>
          <nav aria-label="Privacy policy sections">
            <p>Contents</p>
            <a href="#privacy-scope">Scope</a>
            <a href="#privacy-information">Information we collect</a>
            <a href="#privacy-use">How we use information</a>
            <a href="#privacy-cookies">Cookies</a>
            <a href="#privacy-sharing">Sharing and transfers</a>
            <a href="#privacy-retention">Retention and security</a>
            <a href="#privacy-rights">Your choices and rights</a>
            <a href="#privacy-contact">Contact us</a>
          </nav>
        </aside>

        <article className="privacy-page__content">
          <section id="privacy-scope" aria-labelledby="privacy-scope-title">
            <span>01</span>
            <h2 id="privacy-scope-title">Scope and responsibility</h2>
            <p>
              This policy applies to information collected through this website,
              our digital customer service channels, and related order services.
              It does not cover third-party websites that operate under their own
              privacy notices.
            </p>
            <p>
              The entity identified in your order confirmation is responsible for
              the personal information connected with that purchase. References to
              "we," "us," and "our" in this policy mean {siteConfig.brandName} and
              the service providers acting on its instructions.
            </p>
          </section>

          <section id="privacy-information" aria-labelledby="privacy-information-title">
            <span>02</span>
            <h2 id="privacy-information-title">Information we collect</h2>
            <p>We collect information that you provide and limited data generated through use of the site, including:</p>
            <ul>
              <li>Identity and contact details, such as your name, email and delivery address.</li>
              <li>Order and payment details, including purchased items, delivery selections, and transaction status.</li>
              <li>Preference data, including saved items and sizes.</li>
              <li>Customer service correspondence and information you include in an enquiry or return request.</li>
              <li>Technical data such as device type, browser, approximate location, IP address, and site interactions.</li>
            </ul>
            <p>
              Payment card details are handled by authorized payment providers. We
              receive transaction confirmation and limited payment identifiers,
              rather than complete card numbers.
            </p>
          </section>

          <section id="privacy-use" aria-labelledby="privacy-use-title">
            <span>03</span>
            <h2 id="privacy-use-title">How we use information</h2>
            <p>Personal information is used when necessary to:</p>
            <ul>
              <li>Process payment, fulfill orders, arrange returns, and provide order updates.</li>
              <li>Respond to questions and provide tailored client assistance.</li>
              <li>Maintain accounts, wish lists, and preferences requested by you.</li>
              <li>Prevent fraud, protect our services, and meet accounting or legal obligations.</li>
              <li>Understand site performance and improve products, content, and customer experience.</li>
            </ul>
            <p>
              Depending on the context, our legal basis may be performance of a
              contract, compliance with law, legitimate business interests, or
              consent. You may withdraw consent at any time without affecting prior
              processing.
            </p>
          </section>

          <section id="privacy-cookies" aria-labelledby="privacy-cookies-title">
            <span>04</span>
            <h2 id="privacy-cookies-title">Cookies and similar technologies</h2>
            <p>
              Cookies help retain your cart, support secure checkout, remember
              preferences, measure traffic, and understand how content performs.
              Essential cookies operate because they are required for the service.
              Where consent is required, analytics or advertising technologies are
              used only after you make a choice.
            </p>
            <p>
              Browser controls can remove or block cookies, although doing so may
              limit sign-in, checkout, and other website functions. Available
              consent controls can also be used to revise optional cookie choices.
            </p>
          </section>

          <section id="privacy-sharing" aria-labelledby="privacy-sharing-title">
            <span>05</span>
            <h2 id="privacy-sharing-title">Sharing and international transfers</h2>
            <p>
              We share only the information needed by service providers that
              support hosting, payment, fraud prevention, fulfillment, delivery,
              analytics, communications, and customer care. Information may also be
              disclosed to professional advisers, regulators, or authorities where
              required, or as part of a legitimate business reorganization.
            </p>
            <p>
              Some recipients may process information outside your home country.
              Where required, we use approved contractual terms or another lawful
              transfer mechanism and assess appropriate safeguards.
            </p>
          </section>

          <section id="privacy-retention" aria-labelledby="privacy-retention-title">
            <span>06</span>
            <h2 id="privacy-retention-title">Retention and security</h2>
            <p>
              Information is retained only as long as needed for the purpose for
              which it was collected, including customer service, warranty,
              accounting, fraud prevention, and legal requirements. Retention
              periods vary according to record type and applicable law.
            </p>
            <p>
              We use organizational and technical measures intended to protect
              information against unauthorized access, alteration, loss, or misuse.
              No online transmission is entirely risk-free, so we also encourage
              strong passwords and care when sharing account credentials.
            </p>
          </section>

          <section id="privacy-rights" aria-labelledby="privacy-rights-title">
            <span>07</span>
            <h2 id="privacy-rights-title">Your choices and rights</h2>
            <p>
              Depending on your location, you may request access, correction,
              deletion, portability, or restriction of personal information, or
              object to certain processing.
            </p>
            <p>
              We may need to verify your identity before completing a request. An
              authorized representative may submit a request where local law allows.
              You also have the right to raise a concern with the privacy regulator
              in your jurisdiction.
            </p>
            <div className="privacy-page__notice">
              <h3>Children</h3>
              <p>
                This website is not directed to children, and we do not knowingly
                collect personal information from children below the age at which
                consent may be given independently under applicable law.
              </p>
            </div>
          </section>

          <section id="privacy-contact" aria-labelledby="privacy-contact-title">
            <span>08</span>
            <h2 id="privacy-contact-title">Changes and contact</h2>
            <p>
              We may revise this policy to reflect changes in our services or legal
              obligations. The date at the top identifies the latest version.
            </p>
            <div className="privacy-page__contact">
              <p>Privacy enquiries</p>
              {contact.email && <a href={`mailto:${contact.email}`}>{contact.email}</a>}
              {address && <address>{address}</address>}
              <Link to="/contact" onClick={toTop}>
                Contact Client Services
              </Link>
            </div>
          </section>
        </article>
      </div>
    </main>
  );
}

export default Privacy;
